import type { CSSProperties } from "react"
import type { Density } from "../contexts/PrefsContext"

// Single source of truth for click FX timing, density, and animation names.
// Both the normal shell (App.tsx) and the App Channel shell
// (discord/AppChannelEntry.tsx) plus BakeScene must import from here.
// No per-file duration literals for click FX are allowed.

// JS cleanup timeouts (must match CSS durations below)
export const FX_FLOATER_MS = 950
export const FX_RIPPLE_MS = 950
export const FX_CRUMB_MS = 950
export const FX_LUNGE_MS = 180
export const FX_PRESS_MS = 120
export const FX_BUMP_MS = 600
export const FX_AUTO_OFF_MS = 280

// Reduced-motion cleanup timeouts (must match .bk-root.rm durations in App.css)
export const FX_REDUCED_FLOATER_MS = 400
export const FX_REDUCED_RIPPLE_MS = 200
export const FX_REDUCED_CRUMB_MS = 200

// Density caps (max retained items)
export const FX_MAX_FLOATERS = 28
export const FX_MAX_RIPPLES = 12
export const FX_MAX_CRUMBS = 60
export const FX_MAX_AUTO_RIPPLES = 6
export const FX_CRUMBS_PER_CLICK = 4
export const FX_CRUMBS_PER_CLICK_LOW = 2

// Shared animation names (must match @keyframes in App.css)
export const FX_ANIM_RIPPLE = "bk-rip"
export const FX_ANIM_RIPPLE_AUTO = "bk-rip-auto"
export const FX_ANIM_CRUMB = "bk-crumb"
export const FX_ANIM_FLOATER = "bk-floatup"
export const FX_ANIM_LUNGE = "bk-lunge-dir"
export const FX_ANIM_AUTO = "bk-auto"

// Shared class names for FX layers
export const FX_CLASS_RIPPLE = "bk-ripple"
export const FX_CLASS_CRUMB = "bk-crumb"
export const FX_CLASS_FLOAT = "bk-float"

export function fxIsOff(density: Density): boolean {
  return density === "off"
}

// Gate idle vs clicking: only show lunge/ripple when actually clicking.
// Use with lunging state and density to ensure idle shows no FX.
export function fxShouldLunge(density: Density, reduced: boolean | undefined, isClicking: boolean): boolean {
  if (reduced) return false
  if (fxIsOff(density)) return false
  return isClicking
}

export function fxCrumbsFor(density: Density): number {
  if (density === "off") return 0
  if (density === "low") return FX_CRUMBS_PER_CLICK_LOW
  return FX_CRUMBS_PER_CLICK
}

export function fxFloaterMs(reduced: boolean): number {
  return reduced ? FX_REDUCED_FLOATER_MS : FX_FLOATER_MS
}

export function fxRippleMs(reduced: boolean): number {
  return reduced ? FX_REDUCED_RIPPLE_MS : FX_RIPPLE_MS
}

export function fxCrumbMs(reduced: boolean): number {
  return reduced ? FX_REDUCED_CRUMB_MS : FX_CRUMB_MS
}

// CSS custom properties mirroring the JS constants above.
// Spread onto .bk-root (or :root) so App.css durations stay in sync.
export function fxCssVars(): CSSProperties {
  return {
    ["--fx-floater" as string]: `${FX_FLOATER_MS}ms`,
    ["--fx-ripple" as string]: `${FX_RIPPLE_MS}ms`,
    ["--fx-crumb" as string]: `${FX_CRUMB_MS}ms`,
    ["--fx-lunge" as string]: `${FX_LUNGE_MS}ms`,
    ["--fx-press" as string]: `${FX_PRESS_MS}ms`,
    ["--fx-bump" as string]: `${FX_BUMP_MS}ms`,
    ["--fx-auto" as string]: `${FX_AUTO_OFF_MS}ms`,
    ["--fx-floater-reduced" as string]: `${FX_REDUCED_FLOATER_MS}ms`,
    ["--fx-ripple-reduced" as string]: `${FX_REDUCED_RIPPLE_MS}ms`,
    ["--fx-crumb-reduced" as string]: `${FX_REDUCED_CRUMB_MS}ms`,
  } as CSSProperties
}
